import { convertPolygonCoordsToOpenLayers, FlatPolygonCoordinates, getFlatCoordinatesIfPolygon, isPolygonFlatCoordinatesArray } from "./geom/flatPolygonCoords"
import { convertGeoJsonToGeometry, convertGeometryToGeoJson, GeoJsonGeometry, isGeojsonGeometry } from "./geom/geojson"
import { convertBase64WellknownBinaryToGeometry } from "./geom/wkb"
import { isWellKnownText } from "./geom/wkt"
import { isBase64 } from "./utils/base64"
import { isString } from "./utils/utils"
import { ol_polygonFromCircle, ol_geom, ol_Feature, ol_format } from "@omnia/exports/openlayers";

const wktFormat = new ol_format.WKT()

export interface IGeometryInstance {
    readonly CoordinateSystemId: number
    readonly WellKnownText: string
}

export interface IGeometry {
    readonly Geometry: IGeometryInstance
}

export type WellKnownTextString = string


export type OmniaApiGeometry = IGeometry | IGeometryInstance | string

export type Geometry2 = IGeometry | IGeometryInstance | WellKnownTextString | FlatPolygonCoordinates | GeoJsonGeometry

export type Geometry3 = Geometry2 | ol_geom.Geometry

/**
 * Returns the geometry of a geometry, only if this geometry is a polygon
 * @param feature Feature
 * @returns Polygon
 */
export function getFeatureGeometryIfPolygon(feature: ol_Feature): ol_geom.Polygon {

    const geom = feature?.getGeometry()

    if (geom instanceof ol_geom.Polygon) {
        return geom
    }

}

/**
 * Returns the WKT representation of the geometry of a feature
 * @param feature Feature
 * @returns WKT representation
 */
export function convertFeatureToWkt(feature: ol_Feature) {

    return feature ? convertGeometryToWkt(feature.getGeometry()) : undefined

}

/**
 * Returns the WKT representation of a geometry
 * @param geom Geometry
 * @returns WKT representation
 */
export function convertGeometryToWkt(s: Geometry3): WellKnownTextString {

    if (!s) {
        return
    }

    if (isWellKnownText(s)) {
        return s
    }

    if (isIGeometry(s)) {
        return s.Geometry.WellKnownText
    }

    if (isIGeometryInstance(s)) {
        return s.WellKnownText
    }

    let geom = convertGeometryToOpenLayers(s)

    if (geom) {

        if (geom instanceof ol_geom.Circle) {
            geom = ol_polygonFromCircle(geom)
        }

        return wktFormat.writeGeometry(geom)

    }

}

/**
 * Converts a geometry to a format that can be quickly converted to an OpenLayers geometry instance
 * This should be called on any geometry property returned by the API
 * Does not convert directly to OpenLayers, because we need the returned value to be storable in a RTK store
 * If the geometry describes a polygon, then returns its coordinates as a FlatPolygonCoordinates
 * Otherwise, returns it as a GeoJSON object
 */
export function prepareGeometry(geometry: Geometry2): Geometry2 {

    if (!geometry) {
        return geometry
    }

    if (isPolygonFlatCoordinatesArray(geometry)) {
        return geometry
    }

    if (isGeojsonGeometry(geometry)) {
        return geometry
    }

    const coords = getFlatCoordinatesIfPolygon(geometry)

    if (coords) {
        return coords
    }

    const olGeometry = convertGeometryToOpenLayers(geometry)

    if (olGeometry) {
        return convertGeometryToGeoJson(olGeometry)
    }

    return geometry

}

export function convertGeometryToOpenLayers(s: Geometry3, defaultProjection = "EPSG:3857"): ol_geom.Geometry {

    if (!s) {
        return
    }

    if (s instanceof ol_geom.Geometry) {
        return s
    }

    if (isPolygonFlatCoordinatesArray(s)) {
        return convertPolygonCoordsToOpenLayers(s)
    }

    if (isGeojsonGeometry(s)) {
        return convertGeoJsonToGeometry(s)
    }

    let wkt: string = undefined

    const projection = { dataProjection: "", featureProjection: "EPSG:3857" }

    if (isString(s)) {

        if (isWellKnownText(s)) {
            wkt = s
            projection.dataProjection = defaultProjection
        }

        if (isBase64(s)) {
            return convertBase64WellknownBinaryToGeometry(s)
        }
    }

    else {

        s = isIGeometry(s)
            ? s.Geometry
            : s

        if (isIGeometryInstance(s)) {

            if (s.CoordinateSystemId) {
                projection.dataProjection = "EPSG:" + s.CoordinateSystemId
            }
            else {
                projection.dataProjection = defaultProjection
            }

            wkt = s.WellKnownText

        }

    }

    if (wkt) {
        return wktFormat.readGeometry(wkt, projection)
    }


}

function isIGeometry(arg: any): arg is IGeometry {
    return (typeof arg === "object") && isIGeometryInstance(arg.Geometry)
}

function isIGeometryInstance(arg: any): arg is IGeometryInstance {
    return (typeof arg === "object") && isString(arg.WellKnownText)
}

export function isPolygon(target: { getType?(): string }) {
    const getType = target?.getType
    return getType && (getType() == "Polygon")
}
